'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Routes } from '@/app/enums';
import { PropsWithClassname } from '@/app/types';

import Avatar, { AvatarSize, AvatarStyle } from '@/app/components/Avatar/Avatar';
import Link from 'next/link';
import { useMemo } from 'react';
import { User } from '@/src/api/models';

export interface ProfileNavigationHeaderProps extends PropsWithClassname {
  currentUser: User;
}

const ProfileNavigationHeader = ({ currentUser, className }: ProfileNavigationHeaderProps) => {
  const { t } = useTranslation();

  const displayedName = useMemo(() => {
    if (!currentUser.firstName && !currentUser.lastName && !currentUser.fullName) {
      return currentUser.email;
    }

    const assembledFullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');

    if (!assembledFullName) {
      return currentUser.fullName;
    }

    return assembledFullName;
  }, [currentUser]);

  return (
    <div className={classNames(className, 'flex items-center')}>
      <Avatar
        placeholderText={currentUser.firstName || currentUser.fullName || currentUser.email}
        className="mr-[16px] lg:mr-[24px]"
        style={AvatarStyle.Primary}
        size={AvatarSize.Small}
      />
      <div>
        <h4 className="text-[20px] font-medium leading-[24px] text-balance">{displayedName}</h4>
        <div className="flex">
          <Link
            href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.EDIT_PROFILE}`}
            prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
            className="text-primary-mid hover:underline leading-[24px] text-[14px]"
          >
            {t('components.ProfileNavigationHeader.editProfile')}
          </Link>
          <span className="text-neutral-semiTransparent text-[16px] mx-[8px]">•</span>
          <Link
            href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.CHANGE_PASSWORD}`}
            prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
            className="text-primary-mid hover:underline leading-[24px] text-[14px]"
          >
            {t('components.ProfileNavigationHeader.changePassword')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProfileNavigationHeader;
